/**
 * Round wooden hut. See buildingSmall.ts for the shape of this file and why a coarse building is
 * solid massing rather than a shell.
 *
 * THE LADDER (5-7 m across, 6.3-7.8 m tall — a drum under a cone, which is the one silhouette that
 * survives coarsening almost unchanged):
 *   lod 0   0.25 m  hollow cylinder walls, door, three windows, floor, ceiling, conical roof
 *   lod 1   0.5 m   plinth + wall drum + roof cone (10-14 voxels across, 6-8 roof rows)
 *   lod 2   1 m     the same three parts (5-7 across, 3-4 roof rows)
 *   lod 3   2 m     plinth folded into the drum; the cone is 2 rows
 *   lod 4-5 4-8 m   one column at the roof apex — the hut is 1.75 voxels across at lod 4
 *   lod 6+          nothing (see registry.columnDot)
 */
import { mat } from '../../../materials/index.js';
import { sdfBoxS, sdfCylinderS } from '../../../voxel/shapes.js';
import { blockHalf, buildingSink, roofConeBase, roofTipRadius, } from '../coarseModel.js';
import { conePart, cylinderPart, } from '../../../voxel/buildParts.js';
import { StampType } from '../StampTypes.js';
import { sphere } from '../../../voxel/blobs.js';
import { makeRng } from '../../../util/rng.js';
import { MAX_FOLD_LOD, MAX_SCATTER_LOD } from '../lodLevels.js';
import { SDF_THRESHOLD, pushLamp, pushTorchAt, rotateInv } from '../buildingCommon.js';
function params(variant, seed) {
    // Variation parameters
    const radius = 10 + (variant % 3) * 2; // 10-14 voxels radius
    const wallHeight = 12 + (variant % 2) * 2; // 12-14 voxels
    const roofHeight = radius + 3;
    // Material selection
    const wallMaterials = [mat('wood'), mat('wood2')];
    const roofMaterials = [mat('roof'), mat('roof2'), mat('metal')];
    const floorMaterial = mat('cobble2');
    const foundationMaterial = mat('stone2');
    const wallMaterial = wallMaterials[variant % wallMaterials.length];
    const roofMaterial = roofMaterials[variant % roofMaterials.length];
    const wallThickness = 2;
    const doorHalfWidth = 3;
    const doorHeight = 8;
    const windowY = 5;
    const windowHeight = 4;
    const windowHalfWidth = 3;
    // Bounding box with margin for rotation
    const maxDim = radius + 5;
    const maxHeight = wallHeight + roofHeight + 2;
    const rng = makeRng(seed);
    const lavaRoll = rng();
    const torchRoll = rng();
    return {
        radius, wallHeight, roofHeight,
        wallMaterial, roofMaterial, floorMaterial, foundationMaterial,
        wallThickness, doorHalfWidth, doorHeight, windowY, windowHeight, windowHalfWidth,
        maxDim, maxHeight,
        lavaRoll, torchRoll,
    };
}
/** LOD 0 — the original generateHutSDF body, reading its numbers from params. */
function lod0(p, _variant, seed, rotation) {
    const voxels = [];
    const { radius, wallHeight, roofHeight, wallMaterial, roofMaterial, floorMaterial, foundationMaterial, wallThickness, doorHalfWidth, doorHeight, windowY, windowHeight, windowHalfWidth, maxDim, maxHeight, } = p;
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    // Sample SDF over the entire bounding volume
    for (let y = -2; y <= maxHeight; y++) {
        for (let x = -maxDim; x <= maxDim; x++) {
            for (let z = -maxDim; z <= maxDim; z++) {
                // Rotate query point to local building space (inverse rotation)
                const { x: rx, z: rz } = rotateInv(x, z, cos, sin);
                // Evaluate what's at this rotated position using proper SDFs
                const hit = evaluateHutSDF(rx, y, rz, radius, wallHeight, roofHeight, wallThickness, doorHalfWidth, doorHeight, windowY, windowHeight, windowHalfWidth, wallMaterial, foundationMaterial, floorMaterial, roofMaterial);
                if (hit) {
                    voxels.push({
                        x, y, z,
                        material: SAMPLE.material,
                        weight: SAMPLE.weight,
                    });
                }
            }
        }
    }
    // --- Decorations (deterministic per building) ---
    // Rolls from params, in draw order — see buildingSmall.lod0.
    // Ceiling lava blob on 50% of huts.
    if (p.lavaRoll < 0.5) {
        for (const v of sphere(0, wallHeight - 2, 0, 2.5, mat('lava'), 0.4, seed))
            voxels.push(v);
    }
    // Exterior torches flanking the door (front = -Z) on 25% of huts.
    if (p.torchRoll < 0.25) {
        const zOuter = -radius;
        pushTorchAt(voxels, -(doorHalfWidth + 1), zOuter, doorHeight, cos, sin);
        pushTorchAt(voxels, doorHalfWidth + 1, zOuter, doorHeight, cos, sin);
    }
    return voxels;
}
/** Top of the LOD-0 silhouette: the roof apex. Seed-independent (the size is variant-derived). */
function topFine(p) {
    return p.wallHeight + p.roofHeight;
}
function lodParts(p, lod) {
    const stride = 1 << lod;
    const top = topFine(p);
    const sink = buildingSink(p.radius, stride);
    if (lod <= 3) {
        // THE WALL RUNS ONE STRIDE PAST THE EAVE, up inside the roof — that is where the parts' overlap
        // comes from (coarseModel rule 1), and it is why the cone can state its real base radius at the real
        // eave. Sinking the CONE instead put its base below the wall top, where it had already tapered:
        // the drawn roof radius at the eave measured 8.9 fine voxels against LOD 0's 11.7, i.e. a cap
        // sunk into its own drum, which is what "the roof is too small" looks like.
        //
        // ROOF FIRST IN THE LIST, so it owns the rows it shares with the wall (the composite gives ties to
        // the earliest part) — those rows are the eave, and the eave belongs to the roof.
        const tipR = roofTipRadius(p.radius + 1, p.wallHeight, top, stride);
        const parts = [
            conePart(roofConeBase(p.radius + 1, p.radius, tipR, p.wallHeight, top, stride), tipR, p.wallHeight, top, p.roofMaterial),
        ];
        if (lod <= 2) {
            parts.push(cylinderPart(p.radius + 1, sink + 1, p.foundationMaterial, -sink));
            parts.push(cylinderPart(p.radius, p.wallHeight + stride, p.wallMaterial, 0));
        }
        else {
            parts.push(cylinderPart(p.radius, p.wallHeight + stride + sink, p.wallMaterial, -sink));
        }
        pushLamp(parts, hasLamp(p), p.doorHeight, p.radius, lod, stride);
        return parts;
    }
    // lod 4-5: ONE BLOB, plus one voxel of light beside it if this building is a lit one — the trees'
    // answer at these strides, with one difference that matters. A tree is genuinely narrower than a
    // lod-4 voxel, so its blob is a single column; a 12 m house is three voxels wide, and drawing it as
    // one would have it swell to three times its size as you cross into lod 3. `blockHalf` sizes the blob
    // to the building and converges on a single voxel by lod 5 on its own.
    const blob = [cylinderPart(blockHalf(p.radius, stride), top + sink, p.roofMaterial, -sink)];
    pushLamp(blob, hasLamp(p), p.doorHeight, p.radius, lod, stride);
    return blob;
}
/**
 * Whether this instance carries exterior torches — LOD 0's own roll, read in one place because the
 * coarse cache keys on it (see coarseSeedClass). The FRACTION is this building's own decision; what
 * a lamp then IS at a coarse level is shared (buildingCommon.pushLamp).
 */
const LAMP_CHANCE = 0.25;
function hasLamp(p) {
    return p.torchRoll < LAMP_CHANCE;
}
export const BUILDING_HUT_SPEC = {
    type: StampType.BUILDING_HUT,
    maxLod: MAX_SCATTER_LOD,
    foldLod: MAX_FOLD_LOD,
    params,
    lod0,
    lodParts,
    // The lamp is the one seed-derived fact the coarse model reads, so it is the whole shape class:
    // without this the cache would serve a lit building's model to an unlit one and vice versa (caught
    // by lodStandIn's seed-class case).
    coarseSeedClass: (seed) => (hasLamp(params(0, seed)) ? 1 : 0),
};
/**
 * The evaluator's result, as a scratch rather than a return object: it is read ~95k times per placed
 * building (the sample loop in lod0), and a `{ material, weight }` per hit was the bulk of that cost.
 * Written on `true`, undefined on `false`.
 */
const SAMPLE = { material: 0, weight: 0 };
/**
 * Evaluate hut using proper SDF at a point in local (unrotated) space
 */
function evaluateHutSDF(x, y, z, radius, wallHeight, roofHeight, wallThickness, doorHalfWidth, doorHeight, windowY, windowHeight, windowHalfWidth, wallMaterial, foundationMaterial, floorMaterial, roofMaterial) {
    // Foundation SDF (-2 to 0)
    if (y >= -2 && y < 0) {
        const foundationDist = sdfCylinderS(x, y + 1, z, radius + 1, 1);
        if (foundationDist < SDF_THRESHOLD) {
            {
                SAMPLE.material = foundationMaterial;
                SAMPLE.weight = 0.45;
                return true;
            }
        }
        // Stairs in front of door
        const stairStep = -y - 1;
        if (stairStep >= 0 && stairStep < 4) {
            const stairZ = -radius - 1.5 - stairStep;
            const stairDist = sdfBoxS(x, 0, z - stairZ, doorHalfWidth, 0.5, 0.5);
            if (stairDist < SDF_THRESHOLD) {
                {
                    SAMPLE.material = foundationMaterial;
                    SAMPLE.weight = 0.45;
                    return true;
                }
            }
        }
    }
    // Doorway carving - extends through wall and slightly in front to clear terrain
    if (y >= 0 && y < doorHeight) {
        const doorwayDist = sdfBoxS(x, y - doorHeight / 2, z + radius + 1, doorHalfWidth + 0.5, doorHeight / 2, wallThickness + 2);
        if (doorwayDist < SDF_THRESHOLD) {
            {
                SAMPLE.material = 0;
                SAMPLE.weight = -0.5;
                return true;
            } // Carve out doorway
        }
    }
    // Floor (y = 0)
    if (y >= 0 && y < 1) {
        const floorDist = sdfCylinderS(x, y - 0.5, z, radius, 0.5);
        if (floorDist < SDF_THRESHOLD) {
            {
                SAMPLE.material = floorMaterial;
                SAMPLE.weight = 0.45;
                return true;
            }
        }
    }
    // Interior air
    if (y >= 1 && y < wallHeight - 1) {
        const interiorDist = sdfCylinderS(x, y - wallHeight / 2, z, radius - wallThickness, wallHeight / 2);
        if (interiorDist < -SDF_THRESHOLD) {
            {
                SAMPLE.material = 0;
                SAMPLE.weight = -0.5;
                return true;
            }
        }
    }
    // Ceiling
    if (y >= wallHeight - 2 && y < wallHeight) {
        const ceilingDist = sdfCylinderS(x, y - wallHeight + 1, z, radius - wallThickness, 1);
        if (ceilingDist < SDF_THRESHOLD) {
            {
                SAMPLE.material = mat('concrete');
                SAMPLE.weight = 0.45;
                return true;
            }
        }
    }
    // Walls (hollow cylinder)
    if (y >= 1 && y < wallHeight) {
        const outerDist = sdfCylinderS(x, y - wallHeight / 2, z, radius, wallHeight / 2);
        const innerDist = sdfCylinderS(x, y - wallHeight / 2, z, radius - wallThickness, wallHeight / 2 + 1);
        // Wall shell: inside outer, outside inner
        if (outerDist < SDF_THRESHOLD && innerDist > -SDF_THRESHOLD) {
            // Door opening (negative Z side)
            if (z < -radius + wallThickness + SDF_THRESHOLD && y < doorHeight) {
                const doorDist = sdfBoxS(x, y - doorHeight / 2, z + radius, doorHalfWidth, doorHeight / 2, wallThickness + 1);
                if (doorDist < SDF_THRESHOLD) {
                    return false;
                }
            }
            // Window openings - carve terrain like interior
            const inWindowY = y >= windowY && y < windowY + windowHeight;
            if (inWindowY) {
                // Left window (negative X)
                if (x < -radius + wallThickness + SDF_THRESHOLD) {
                    const windowDist = sdfBoxS(x + radius, y - windowY - windowHeight / 2, z, wallThickness + 2, windowHeight / 2, windowHalfWidth);
                    if (windowDist < SDF_THRESHOLD) {
                        {
                            SAMPLE.material = 0;
                            SAMPLE.weight = -0.5;
                            return true;
                        } // Carve window opening
                    }
                }
                // Right window (positive X)
                if (x > radius - wallThickness - SDF_THRESHOLD) {
                    const windowDist = sdfBoxS(x - radius, y - windowY - windowHeight / 2, z, wallThickness + 2, windowHeight / 2, windowHalfWidth);
                    if (windowDist < SDF_THRESHOLD) {
                        {
                            SAMPLE.material = 0;
                            SAMPLE.weight = -0.5;
                            return true;
                        } // Carve window opening
                    }
                }
                // Back window (positive Z)
                if (z > radius - wallThickness - SDF_THRESHOLD) {
                    const windowDist = sdfBoxS(x, y - windowY - windowHeight / 2, z - radius, windowHalfWidth, windowHeight / 2, wallThickness + 2);
                    if (windowDist < SDF_THRESHOLD) {
                        {
                            SAMPLE.material = 0;
                            SAMPLE.weight = -0.5;
                            return true;
                        } // Carve window opening
                    }
                }
            }
            {
                SAMPLE.material = wallMaterial;
                SAMPLE.weight = 0.45;
                return true;
            }
        }
    }
    // Conical roof
    if (y >= wallHeight && y < wallHeight + roofHeight) {
        const roofY = y - wallHeight;
        const roofRadius = radius + 1 - (roofY * (radius + 1) / roofHeight);
        const roofDist = sdfCylinderS(x, 0, z, roofRadius, 0.5);
        if (roofDist < SDF_THRESHOLD) {
            {
                SAMPLE.material = roofMaterial;
                SAMPLE.weight = 0.45;
                return true;
            }
        }
    }
    return false;
}
//# sourceMappingURL=buildingHut.js.map